
import { Injectable } from "@angular/core";
import * as CryptoJS from "crypto-js";
import { HttpParams, HttpRequest } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class SignatureService {

  /**
   * Exclude urls from signing
   * @type {string[]}
   * @private
   */
  private readonly _excludeUrls: string[] = [];

  /**
   * Method to exclude from signing
   * @type {string[]}
   * @private
   */
  private _excludeMethods: string[] = ["HEAD", "OPTIONS"];

  /**
   * The version of the Signature Service
   * @type {string}
   * @private
   */
  private _version: string = "1.0.0";

  /**
   * Returns the version of the Signature Service
   * @returns {string}
   */
  get version(): string {
    return this._version;
  }

  /**
   * The secret key used to sign the request
   * @type {string}
   * @private
   */
  private _secretKey: string = null;

  /**
   * Sets the secret key used to sign the request
   * @param {string} value
   */
  set secretKey(value: string) {
    if (value === null || value === undefined) {
      throw new Error("The secret key cannot be null or undefined");
    }
    if (typeof value !== "string") {
      throw new Error("The secret key must be a string");
    }
    this._secretKey = value;
  }

  constructor() {}



  /**
   * Signs the request
   * @param request
   * @returns
   */
  public signRequest<T = any>(request: HttpRequest<T>): HttpRequest<T> {
    if (this._excludeMethods.indexOf(request.method) > -1 || this._excludeUrls.some(eu => new RegExp(eu).test(request.url))) {
      return request;
    }

    if (this._secretKey === null) {
      throw new Error("The secret key has not been set");
    }

    const method = request.method;
    const url = new URL(request.url).pathname;
    let queryParams = new URL(request.url).search;

    let params = new HttpParams({ fromString: queryParams.replace(/^\?/, '') });

    // combine query params with request params
    request.params.keys().forEach(key => {
      params = params.set(key, request.params.get(key));
    });
    const qParams = this.normalizeQueryParams(params);
    queryParams = qParams ? `?${qParams}` : '';

    let body = null;
    if (["HEAD", "OPTIONS"].indexOf(method) === -1) {
      if (request.body instanceof FormData) {
        body = {};
        request.body.forEach((value, key) => {
          if (typeof value != "object")
            body[key] = value;
        })
        body = JSON.stringify(body);
      } else if (typeof request.body == "object") {
        body = JSON.stringify(request.body || {});
      } else {
        body = request.body as unknown as string;
      }
    }
    body = body || '{}';

    const timestamp = new Date().getTime();
    const signature = this.getSignature(method, url, queryParams, body, timestamp);
    return request.clone({
      setHeaders: {
        "X-sk-Signature": signature,
        "X-sk-Timestamp": timestamp.toString(),
        "X-sk-Version": this._version,
        "X-sk-Window": "No"
      }
    });
  }

  /**
   * Returns the signature for the request
   * @param {string} method
   * @param {string} url
   * @param {string} queryParams
   * @param body
   * @param {number} timestamp
   * @returns {string}
   * @private
   */
  private getSignature(method: string, url: string, queryParams: string, body: any, timestamp: number): string {
    const message = encodeURIComponent(`${method.toUpperCase()}${url}${queryParams}${body ? body : ''}+${timestamp}`);
    return this.hmacSha256(message, this._secretKey);
  }

  /**
   * Returns the HMAC SHA256 hash of the message
   * @param {string} message
   * @param {string} secret
   * @returns {string}
   * @private
   */
  private hmacSha256(message: string, secret: string): string {
    const key = CryptoJS.enc.Utf8.parse(secret);
    const hash = CryptoJS.HmacSHA256(message, key);
    return hash.toString(CryptoJS.enc.Base64);
  }

  /**
   * Normalizes the query parameters by sorting and encoding them
   * @param queryParams
   * @returns {string}
   */
  private normalizeQueryParams(queryParams: HttpParams): string {
    const sortedKeys = Array.from(new Set(queryParams.keys())).sort();
    return sortedKeys.map(key => {
      const value = queryParams.getAll(key);
      if (Array.isArray(value)) {
        return value.map(v => `${key}=${encodeURIComponent(v)}`).sort().join('&');
      }
      return `${key}=${encodeURIComponent(value)}`;
    }).join('&');
  }
}
